import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Calendar, MapPin, Phone, IndianRupee, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { createBooking } from "@/lib/api/bookings.functions";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/_authenticated/book")({
  head: () => ({
    meta: [
      { title: "Book a Performance · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Book Sanchayeeta Seal for your event. Pay securely via UPI and submit your booking request." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookPage,
});

const schema = z.object({
  event_name: z.string().trim().min(2).max(200),
  event_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a date"),
  location: z.string().trim().min(2).max(300),
  contact_phone: z.string().regex(/^[6-9]\d{9}$/, "10-digit mobile"),
  organiser_email: z.string().trim().email("Enter a valid email").max(254),
  amount: z.coerce.number().min(0).max(10000000),
  upi_txn_id: z.string().trim().max(100).optional().or(z.literal("")),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
});

function BookPage() {
  const submit = useServerFn(createBooking);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { event_name: "", event_date: "", location: "", contact_phone: "", organiser_email: "", amount: 0, upi_txn_id: "", notes: "" },
  });

  const amount = form.watch("amount");
  const eventName = form.watch("event_name");

  useEffect(() => {
    const a = Number(amount) || 0;
    const params = new URLSearchParams({
      pa: SITE.upi.id, pn: SITE.upi.payeeName,
      tn: `Booking: ${eventName || "Surer Adda"}`.slice(0, 60),
      cu: "INR",
    });
    if (a > 0) params.set("am", a.toFixed(2));
    const upi = `upi://pay?${params.toString()}`;
    QRCode.toDataURL(upi, { width: 320, margin: 1, color: { dark: "#5a1a22", light: "#ffffff" } })
      .then(setQrUrl).catch(() => setQrUrl(null));
  }, [amount, eventName]);

  const onSubmit = async (d: z.infer<typeof schema>) => {
    try {
      await submit({ data: d });
      toast.success("Booking submitted! We'll confirm shortly.");
      form.reset();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Booking</p>
          <h1 className="mt-2 text-4xl sm:text-5xl">Book a Performance</h1>
          <p className="mt-3 text-muted-foreground">Pay via UPI, then submit your booking — we'll confirm within 24 hours.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <Row label="Event name" icon={<Calendar className="h-4 w-4" />}>
              <Input {...form.register("event_name")} placeholder="Wedding, Puja, Corporate event…" />
              <Err msg={form.formState.errors.event_name?.message} />
            </Row>
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Event date">
                <Input type="date" {...form.register("event_date")} min={new Date().toISOString().slice(0, 10)} />
                <Err msg={form.formState.errors.event_date?.message} />
              </Row>
              <Row label="Contact mobile" icon={<Phone className="h-4 w-4" />}>
                <Input maxLength={10} {...form.register("contact_phone")} placeholder="10 digits" />
                <Err msg={form.formState.errors.contact_phone?.message} />
              </Row>
            </div>
            <Row label="Venue / Location" icon={<MapPin className="h-4 w-4" />}>
              <Input {...form.register("location")} placeholder="City, venue, full address" />
              <Err msg={form.formState.errors.location?.message} />
            </Row>
            <Row label="Organiser email" icon={<Mail className="h-4 w-4" />}>
              <Input type="email" {...form.register("organiser_email")} placeholder="organiser@example.com" />
              <Err msg={form.formState.errors.organiser_email?.message} />
            </Row>
            <Row label="Advance / Amount (INR)" icon={<IndianRupee className="h-4 w-4" />}>
              <Input type="number" min={0} step="100" {...form.register("amount")} />
            </Row>
            <Row label="UPI Transaction ID (after payment)">
              <Input {...form.register("upi_txn_id")} placeholder="From your UPI app receipt" />
            </Row>
            <Row label="Additional notes">
              <Textarea rows={3} {...form.register("notes")} placeholder="Anything else we should know?" />
            </Row>
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full bg-primary text-primary-foreground">
              {form.formState.isSubmitting ? "Submitting…" : "Submit Booking"}
            </Button>
          </form>

          <aside className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Pay via UPI</p>
            <h2 className="mt-1 text-2xl">Scan & Pay</h2>
            <div className="mx-auto mt-6 flex aspect-square w-full max-w-[280px] items-center justify-center rounded-xl border border-border bg-white p-4">
              {qrUrl ? <img src={qrUrl} alt="UPI QR" className="h-full w-full" /> : <p className="text-sm text-muted-foreground">Generating…</p>}
            </div>
            <p className="mt-4 text-sm font-semibold">{SITE.upi.payeeName}</p>
            <p className="text-xs text-muted-foreground">UPI ID: {SITE.upi.id}</p>
            <p className="mt-4 text-xs text-muted-foreground">
              After paying, copy the UPI transaction ID into the form and submit. Your booking is confirmed once payment is verified.
            </p>
          </aside>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function Row({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </Label>
      {children}
    </div>
  );
}
function Err({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-destructive">{msg}</p>;
}
